import { deleteDoc, type DocumentReference } from 'firebase/firestore';

import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentStatus } from 'types/DocumentStatus';
import { isSuperUserSiteRole } from 'types/Roles';
import { formatAdaptiveRelativeTime } from 'utils/Dates';
import {
    nowTimestamp,
    nullTimestamp,
    timestampCompare,
    timestampToDate,
} from 'utils/FirestoreUtils';
import { djb2 } from 'utils/Hash';

import type CommentsManager from './CommentsManager';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';
import root from './RootStore';
import { type UserSchemaPublic } from './User';

function hueForString(seed: string) {
    return djb2(seed) % 360;
}

function colorForString(seed: string) {
    const hue = hueForString(seed);
    return `hsl(${hue}, 50%, 90%)`;
}

export class CommentSchema extends DocumentSchema {
    timestamp = nullTimestamp();
    lastModified = nullTimestamp();
    placeholderName = '';
    owner = '';
    content = '';
    playheadPosition: number | null = null;
    ownerMetadata: UserSchemaPublic;
}

class Comment extends DocumentStore<CommentSchema> {
    schema = new CommentSchema();
    collectionType = DocumentCollectionType.COMMENTS;

    uid = '';
    editing = false;
    selected = false;
    ownerDisplayName = '';
    ownerPhotoURL = '';
    datetime = '';
    thumbnailColor = '';
    // parent: CommentsManager; // redeclaration removed

    constructor(parent: CommentsManager, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable();
    }

    get edited() {
        return timestampCompare(this.data.lastModified, this.data.timestamp) > 0;
    }

    get canEdit() {
        return (
            root.user.data.uid === this.data.owner ||
            isSuperUserSiteRole(root.user.role)
        );
    }

    onReady = async () => {
        this.updateDatetime();
        await this.updateOwnerInfo();
        this.updateThumbnailColor();
    }

    onLoad = async () => {
        this.status = DocumentStatus.LOADING;

        this.updateDatetime();
        await this.updateOwnerInfo();
        this.updateThumbnailColor();

        this.status = DocumentStatus.LOADED;
    }

    // FIXME never called: DocumentStore's update hook is onUpdated, not
    // onUpdate, so nothing invokes this. A modified comment therefore never
    // refreshes its datetime or owner info through here. (Template and Bundle
    // declare the same dead onUpdate.) Renaming it to onUpdated would make it
    // live, which is a behavior change - check what an edit currently shows
    // first.
    onUpdate = (_data: CommentSchema) => {
        this.updateDatetime();
        this.updateOwnerInfo();
    };

    edit = async (content: string) => {
        this.data.content = content;
        this.data.lastModified = nowTimestamp();
        this.save();
    };

    setContent = (content: string) => {
        this.data.content = content;
        this.data.lastModified = nowTimestamp();
    }

    selectToggle = () => {
        this.selected = !this.selected;
    }

    select = (skipUpdate: boolean) => {
        this.selected = true;
        if (skipUpdate) return;
    }

    deselect = (skipUpdate: boolean) => {
        this.selected = false;
        if (skipUpdate) return;
    }

    delete = async () => {
        await deleteDoc(this.docRef);
    }

    updateOwnerInfo = async () => {
        if (this.data.placeholderName === '' && this.data.owner !== '') {
            try {
                const user = await root.usersCache.getById(this.data.owner);
                if (user) {
                    this.ownerDisplayName = user.data.displayName || '';
                    this.ownerPhotoURL = user.data.cachedPhotoURL;
                } else {
                    this.ownerDisplayName = this.data.name;
                    this.ownerPhotoURL = '';
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        } else {
            this.ownerDisplayName = this.data.placeholderName;
        }
    };

    updateDatetime = () => {
        this.datetime = formatAdaptiveRelativeTime(
            timestampToDate(this.data.timestamp),
            new Date()
        );
    }

    updateThumbnailColor = () => {
        if (this.data.placeholderName !== '' && this.data.owner === '') {
            this.thumbnailColor = colorForString(this.data.placeholderName);
        }
    };

    reset = () => {
        this.editing = false;
        this.selected = false;
        this.ownerDisplayName = '';
        this.ownerPhotoURL = '';
        this.datetime = '';
        this.thumbnailColor = '';
    }
}

export default Comment;
